import React, {useEffect, useRef, useState} from 'react';

import cs from 'classnames';

interface Props {
  renderAs?: keyof JSX.IntrinsicElements;
  onClick: (isChecked: boolean) => unknown;
  isChecked: boolean;
  fixedWidth?: boolean;
  children?: React.ReactNode;
  disabled?: boolean;
  id?: string;
  className?: string;
  testid?: string;
}
interface RealCheckboxProps extends Props {
  useRealCheckbox: true;
  isIndeterminate?: boolean;
}

interface NotRealCheckboxProps extends Props {
  useRealCheckbox?: false;
  isIndeterminate?: never;
}

export function CheckboxButton({
  renderAs,
  onClick: propOnClick,
  isChecked: propIsChecked,
  isIndeterminate = false,
  fixedWidth = false,
  children,
  useRealCheckbox = false,
  disabled,
  id,
  className,
  testid,
}: RealCheckboxProps | NotRealCheckboxProps): JSX.Element {
  const [isLoading, setLoading] = useState(false);
  const [isChecked, setChecked] = useState(propIsChecked);
  const checkboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // During loading, only sync if prop matches our optimistic value
    if (isLoading) {
      if (propIsChecked === isChecked) {
        setLoading(() => false);
      }
      return;
    }

    // Normal sync when not loading
    setChecked((current) => {
      return current !== propIsChecked ? propIsChecked : current;
    });
  }, [propIsChecked, isLoading, isChecked]);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  function onClick(): Promise<unknown> {
    if (isLoading) {
      return Promise.resolve();
    }

    setLoading(() => true);
    setChecked(() => !propIsChecked);

    const onClickResponse = propOnClick(!propIsChecked);

    if (!onClickResponse) {
      setLoading(() => false);
      return Promise.resolve();
    }

    return Promise.resolve(onClickResponse).catch((err) => {
      setChecked(() => propIsChecked);
      setLoading(() => false);
      return err;
    });
  }

  if (useRealCheckbox === true) {
    return (
      <input
        type="checkbox"
        disabled={disabled || isLoading}
        ref={checkboxRef}
        checked={isChecked}
        onChange={onClick}
        id={id}
        className={className}
        data-testid={testid}
      />
    );
  }

  const ContainerElement = renderAs || 'button';

  return (
    <ContainerElement
      id={id}
      className={className}
      onClick={onClick}
      disabled={disabled || isLoading}
      data-testid={testid}
    >
      <i
        className={cs(
          'far',
          {
            'far-fw': fixedWidth,
          },
          isChecked ? 'fa-check-square' : 'fa-square'
        )}
      />{' '}
      {children}
    </ContainerElement>
  );
}
